import { useDisclosure } from '@televet/kibble-ui';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  TrupanionAvailability,
  useIssueTrupanionCertificateMutation,
  useTrupanionCertificateAvailabilityQuery,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLFetchPolicies } from 'shared/enums';
import TrupanionAlert from './TrupanionAlert';
import TrupanionModal from './TrupanionModal';
import { useTracking } from 'shared/providers/TrackingProvider';
import { TrupanionEvents } from 'shared/providers/TrackingProvider/EventNames';

export enum ProcessStep {
  Initial = 'initial',
  IssuingCertificate = 'issuingCertificate',
  Loading = 'loading',
  PetSelection = 'petSelection',
  NoAvailablePets = 'noAvailablePets',
  Confirmation = 'confirmation',
}

const TRUPANION_PORTAL_URL = 'https://vetportal.trupanion.com/';
export const openTrupanionPortal = (): void => {
  window.open(TRUPANION_PORTAL_URL, '_blank');
};

type TrupanionCheckoutProps = {
  clinicPetParentId: string;
  showOnlyWhenEligible?: boolean;
};

const TrupanionCheckout = ({ clinicPetParentId, showOnlyWhenEligible }: TrupanionCheckoutProps): JSX.Element => {
  const [processStep, setProcessStep] = useState<ProcessStep>(ProcessStep.Initial);
  const [selectedPets, setSelectedPets] = useState<TrupanionAvailability[]>([]);
  const [isSmsOptedIn, setIsSmsOptedIn] = useState<boolean>(false);
  const [isCertificateError, setIsCertificateError] = useState<boolean>(false);
  const [certificateReferences, setCertificateReferences] = useState<{ [clinicPetId: string]: string }>({});
  const [selectedBreedValues, setSelectedBreedValues] = useState<{ [key: string]: string }>({});
  const { currentClinicId } = useClinicUser();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { trackEvent } = useTracking();

  const { data: trupanionCertificateAvailability, loading: isLoadingEligibility } =
    useTrupanionCertificateAvailabilityQuery({
      variables: { where: { clinicPetParentId: clinicPetParentId || '' } },
      skip: !clinicPetParentId,
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    });

  const eligiblePets = useMemo(() => {
    const response = trupanionCertificateAvailability?.trupanionCertificateAvailability || [];
    const newEligiblePets = response.filter((certificate) => certificate?.isAvailable);
    return newEligiblePets;
  }, [trupanionCertificateAvailability?.trupanionCertificateAvailability]);

  useEffect(() => {
    if (eligiblePets.length > 0) {
      setSelectedPets(eligiblePets as TrupanionAvailability[]);
    }
  }, [eligiblePets]);

  useEffect(() => {
    if (processStep === ProcessStep.PetSelection && eligiblePets.length > 0) {
      trackEvent(TrupanionEvents.OfferEligible);
    }
  }, [processStep, eligiblePets.length, trackEvent]);

  const resetTrupanionState = useCallback((): void => {
    setProcessStep(ProcessStep.PetSelection);
    setSelectedPets(eligiblePets as TrupanionAvailability[]);
  }, [eligiblePets]);

  useEffect(() => {
    return (): void => {
      resetTrupanionState();
    };
  }, [resetTrupanionState, clinicPetParentId]);

  const shouldAlertDisplay = useMemo(() => {
    return !!currentClinicId && !!clinicPetParentId && !(showOnlyWhenEligible && !eligiblePets.length);
  }, [currentClinicId, clinicPetParentId, showOnlyWhenEligible, eligiblePets.length]);

  const deselectPet = (clinicPetId: string): void => {
    const newSelectedPets = selectedPets.filter((pet) => pet.clinicPetId !== clinicPetId);
    setSelectedPets(newSelectedPets);
  };

  const selectPet = (clinicPetId: string): void => {
    if (selectedPets.some((pet) => pet?.clinicPetId === clinicPetId)) {
      deselectPet(clinicPetId);
    } else {
      const certificate = eligiblePets.find((pet) => pet?.clinicPetId === clinicPetId);
      setSelectedPets((prevState) => [...prevState, certificate as TrupanionAvailability]);
    }
  };

  const [issueTrupanionCertificate, { loading: isCertificateLoading }] = useIssueTrupanionCertificateMutation();

  useEffect(() => {
    if (isCertificateLoading) {
      setProcessStep(ProcessStep.Loading);
    }
  }, [isCertificateLoading]);

  useEffect(() => {
    if (isLoadingEligibility) {
      setProcessStep(ProcessStep.Initial);
    } else {
      setProcessStep(ProcessStep.PetSelection);
    }
  }, [eligiblePets.length, isLoadingEligibility]);

  useEffect(() => {
    if (Object.keys(certificateReferences).length > 0) {
      setProcessStep(ProcessStep.Confirmation);
    }
  }, [certificateReferences, selectedPets.length]);

  const toggleSmsOptIn = (): void => {
    setIsSmsOptedIn((prevState) => !prevState);
  };
  const handleSendOffer = useCallback(async (): Promise<void> => {
    trackEvent(TrupanionEvents.OfferSendClicked);
    selectedPets.forEach((availability) => {
      const trupanionBreed = selectedBreedValues[availability.clinicPetId] || availability.trupanionClosestBreed;
      if (availability.clinicPetId && clinicPetParentId) {
        issueTrupanionCertificate({
          variables: {
            where: {
              clinicPetId: availability.clinicPetId,
              clinicPetParentId: clinicPetParentId,
            },
            data: {
              trupanionBreed,
              isSmsOptedIn,
            },
          },
        }).then((response) => {
          const { referenceNumber } = response.data?.trupanionIssueCertificate || {};
          if (!referenceNumber) {
            setIsCertificateError(true);
          }
          setCertificateReferences((prevState) => {
            const newState = { ...prevState };
            newState[availability.clinicPetId] = referenceNumber || '';
            return newState;
          });
        });
      }
    });
  }, [trackEvent, selectedPets, selectedBreedValues, clinicPetParentId, issueTrupanionCertificate, isSmsOptedIn]);

  const onSendOffers = useCallback(() => {
    if (processStep === ProcessStep.PetSelection) {
      handleSendOffer();
      onClose();
    }
  }, [handleSendOffer, onClose, processStep]);

  return (
    <>
      {shouldAlertDisplay && (
        <TrupanionAlert
          selectedPets={selectedPets}
          processStep={processStep}
          eligiblePets={eligiblePets as TrupanionAvailability[]}
          isCertificateError={isCertificateError}
          openModal={(): void => {
            trackEvent(TrupanionEvents.ViewModal);
            onOpen();
          }}
          certificateReferences={certificateReferences}
          clinicPetParentId={clinicPetParentId}
        />
      )}
      <TrupanionModal
        isSmsOptedIn={isSmsOptedIn}
        isOpen={isOpen}
        closeModal={onClose}
        onSendOffers={onSendOffers}
        processStep={processStep}
        selectedPets={selectedPets}
        resetTrupanionState={resetTrupanionState}
        certificateReferences={certificateReferences}
        isCertificateError={isCertificateError}
        eligiblePets={eligiblePets as TrupanionAvailability[]}
        deselectPet={deselectPet}
        selectPet={selectPet}
        selectedBreedValues={selectedBreedValues}
        setSelectedBreedValues={setSelectedBreedValues}
        toggleSmsOptIn={toggleSmsOptIn}
      />
    </>
  );
};

export default TrupanionCheckout;
